import type { Pox5EventTopic } from "@secondlayer/stacks/pox5";
import type { SbtcEventTopic } from "@secondlayer/stacks/sbtc";
import type { InferredTopicSchema } from "@secondlayer/subgraphs";
import { BaseClient, abortError, buildQuery } from "../base.ts";
import type { SecondLayerOptions } from "../base.ts";
import { ValidationError } from "../errors.ts";
import type { TransactionProof } from "../proofs.ts";
import type { ConsumerSink } from "../sinks/types.ts";
import {
	type PageRetryOptions,
	defaultSleep,
	fetchPageWithRetry,
} from "../streams/consumer.ts";
import { type IndexConsumeOptions, consumeIndexFeed } from "./consumer.ts";

export type IndexTip = {
	block_height: number;
	/** Highest height treated as immutable (past the burn-confirmation
	 *  finality boundary). Rows at or below it never reorg — `finalizedOnly`
	 *  consumers gate on this, since Index rows carry no per-event flag. */
	finalized_height: number;
	lag_seconds: number;
};

/**
 * A chain reorg overlapping a returned page's height range. Height-keyed feeds
 * (`/transactions`, `/contract-calls`, `/stacking`) populate this so a consumer
 * can reconcile: roll back every row at `block_height >= fork_point_height`
 * (the whole fork block is replaced, so the rollback is inclusive of the fork
 * height), then re-read the canonical run from the foot of `fork_point_height`.
 * The SDK consumers do exactly this — they rewind to `Cursor.atHeight(
 * fork_point_height)`, an exclusive cursor that re-reads from `fork:0`
 * inclusive. Empty when the page spans no reorg.
 */
export type IndexReorg = {
	id: string;
	detected_at: string;
	fork_point_height: number;
	old_index_block_hash: string | null;
	new_index_block_hash: string | null;
	/** Orphaned cursor span `<block_height>:<tx_index>`, inclusive. */
	orphaned_range: { from: string; to: string };
	/**
	 * First position of the new canonical chain at the fork, `fork:0`
	 * (INCLUSIVE). Not an exclusive resume token — resuming a `(bh,ei) > cursor`
	 * read directly from it would skip `fork:0`. To re-read the new run, rewind
	 * to the foot of `fork_point_height` (`Cursor.atHeight`), not to this value.
	 */
	new_canonical_tip: string;
};

export type FtTransfer = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	tx_id: string;
	tx_index: number;
	event_index: number;
	event_type: "ft_transfer";
	contract_id: string;
	asset_identifier: string;
	sender: string;
	recipient: string;
	amount: string;
};

export type FtTransfersEnvelope<TRow = FtTransfer> = {
	events: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
};

/** Transfer columns that survive any projection — the consume contract
 *  (`cursor`, `block_height`) plus the `event_type` discriminant. */
export type FtTransferAlwaysFields = "cursor" | "block_height" | "event_type";

/** An ft-transfer row narrowed to the requested columns plus the always-present ones. */
export type FtTransferFields<F extends keyof FtTransfer & string> = Pick<
	FtTransfer,
	(F | FtTransferAlwaysFields) & keyof FtTransfer
>;

export type FtTransfersListParams = {
	/**
	 * Columns to return. The server projects the row, so an unrequested column
	 * is physically absent — and the narrowing overload on `list` makes reading
	 * one a compile error rather than `undefined` at runtime. Omitting
	 * `block_time` also lets the server skip the blocks join.
	 */
	fields?: readonly (keyof FtTransfer & string)[];
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	contractId?: string;
	assetIdentifier?: string;
	sender?: string;
	recipient?: string;
	fromHeight?: number;
	toHeight?: number;
};

/** Largest page any Index list route serves. The server clamps `limit` above
 *  this without saying so, so `walk` refuses a bigger `batchSize` up front
 *  rather than paging in silently smaller steps. */
export const INDEX_MAX_PAGE_SIZE = 1000;

/** Options every `walk*` feed shares on top of its list filters. Page fetches
 *  retry with the same `retryCount`/`retryDelay`/`onError` vocabulary as
 *  `consume()`; the tradeoff is that a walk can pause for the retry delays
 *  instead of failing fast on the first 429. */
export type WalkOptions = PageRetryOptions & {
	/** Rows per page request. Default 200, max `INDEX_MAX_PAGE_SIZE` (1000). */
	batchSize?: number;
	/** Stops the walk. The iterator always rejects with the signal's reason (an
	 *  `AbortError` by default), whether the abort lands during a page request
	 *  (which is cancelled) or between yields, so a caller can tell a finished
	 *  walk from a stopped one. */
	signal?: AbortSignal;
};

/** Per-request signal the private list methods thread into `request`. */
type RequestSignal = { signal?: AbortSignal };

export type FtTransfersWalkParams = Omit<FtTransfersListParams, "limit"> &
	WalkOptions;

export type NftTransfer = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	tx_id: string;
	tx_index: number;
	event_index: number;
	event_type: "nft_transfer";
	contract_id: string;
	asset_identifier: string;
	sender: string;
	recipient: string;
	value: string;
};

export type NftTransfersEnvelope<TRow = NftTransfer> = {
	events: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
};

/** See {@link FtTransferAlwaysFields}. */
export type NftTransferAlwaysFields = "cursor" | "block_height" | "event_type";

/** An nft-transfer row narrowed to the requested columns plus the always-present ones. */
export type NftTransferFields<F extends keyof NftTransfer & string> = Pick<
	NftTransfer,
	(F | NftTransferAlwaysFields) & keyof NftTransfer
>;

export type NftTransfersListParams = {
	/** Columns to return (see {@link FtTransfersListParams.fields}). */
	fields?: readonly (keyof NftTransfer & string)[];
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	contractId?: string;
	assetIdentifier?: string;
	sender?: string;
	recipient?: string;
	fromHeight?: number;
	toHeight?: number;
};

export type NftTransfersWalkParams = Omit<NftTransfersListParams, "limit"> &
	WalkOptions;

// ── Generic decoded events (/v1/index/events) ──────────────────────

type IndexEventBase = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	tx_id: string;
	tx_index: number;
	event_index: number;
	contract_id: string | null;
	/** Submitting-transaction context, present only when `txContext: true` was
	 *  requested. `tx_sender` is the real tx sender — distinct from a transfer's
	 *  asset `sender`, and the only place a `print` event's sender is available.
	 *  Lets a consumer build per-event tx context without a `/v1/index/transactions`
	 *  call per event. */
	tx_sender?: string | null;
	tx_type?: string | null;
	tx_status?: string | null;
	tx_contract_id?: string | null;
	tx_function_name?: string | null;
};

export type IndexFtTransfer = IndexEventBase & {
	event_type: "ft_transfer";
	asset_identifier: string;
	sender: string;
	recipient: string;
	amount: string;
};
export type IndexNftTransfer = IndexEventBase & {
	event_type: "nft_transfer";
	asset_identifier: string;
	sender: string;
	recipient: string;
	value: string;
};
export type IndexStxTransfer = IndexEventBase & {
	event_type: "stx_transfer";
	sender: string;
	recipient: string;
	amount: string;
	memo: string | null;
};
export type IndexStxMint = IndexEventBase & {
	event_type: "stx_mint";
	recipient: string;
	amount: string;
};
export type IndexStxBurn = IndexEventBase & {
	event_type: "stx_burn";
	sender: string;
	amount: string;
};
export type IndexStxLock = IndexEventBase & {
	event_type: "stx_lock";
	sender: string;
	amount: string;
	payload: { unlock_height: string | null };
};
export type IndexFtMint = IndexEventBase & {
	event_type: "ft_mint";
	asset_identifier: string;
	recipient: string;
	amount: string;
};
export type IndexFtBurn = IndexEventBase & {
	event_type: "ft_burn";
	asset_identifier: string;
	sender: string;
	amount: string;
};
export type IndexNftMint = IndexEventBase & {
	event_type: "nft_mint";
	asset_identifier: string;
	recipient: string;
	value: string;
};
export type IndexNftBurn = IndexEventBase & {
	event_type: "nft_burn";
	asset_identifier: string;
	sender: string;
	value: string;
};
export type IndexPrint = IndexEventBase & {
	event_type: "print";
	payload: { topic: string | null; value: unknown; raw_value: string | null };
};

/** Decoded chain event, discriminated by `event_type`. */
export type IndexEvent =
	| IndexFtTransfer
	| IndexNftTransfer
	| IndexStxTransfer
	| IndexStxMint
	| IndexStxBurn
	| IndexStxLock
	| IndexFtMint
	| IndexFtBurn
	| IndexNftMint
	| IndexNftBurn
	| IndexPrint;

export type IndexEventType = IndexEvent["event_type"];

/** Fields every read returns regardless of `fields` — the consume contract
 *  (`cursor`, `block_height`) plus the union discriminant. */
export type IndexAlwaysFields = "cursor" | "block_height" | "event_type";

/** An event row narrowed to a `fields` selection (plus what always ships). */
export type IndexEventFields<
	T extends IndexEventType,
	F extends keyof IndexEventOf<T> & string,
> = Pick<IndexEventOf<T>, (F | IndexAlwaysFields) & keyof IndexEventOf<T>>;

export type EventsEnvelope<
	T extends IndexEventType = IndexEventType,
	TRow = IndexEventOf<T>,
> = {
	events: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
};

/** The single decoded-event member matching one `event_type` literal.
 *  `IndexEventOf<"ft_transfer">` is `IndexFtTransfer`. */
export type IndexEventOf<T extends IndexEventType> = Extract<
	IndexEvent,
	{ event_type: T }
>;

export type EventsListParams<T extends IndexEventType = IndexEventType> = {
	/** Required. One of the decoded event types. Passing a literal narrows the
	 *  rows every surface hands back to that event's own shape. */
	eventType: T;
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	/** One id, or several to scope the sweep to a set of contracts (max 20).
	 *  Mutually exclusive with `trait`. */
	contractId?: string | readonly string[];
	assetIdentifier?: string;
	sender?: string;
	recipient?: string;
	fromHeight?: number;
	toHeight?: number;
	/** Restrict to contracts conforming to a trait/standard (e.g. "sip-010").
	 *  Mutually exclusive with contractId; contract-keyed event types only. */
	trait?: string;
	/** Join the submitting transaction into each event — populates `tx_sender`,
	 *  `tx_type`, `tx_status`, `tx_contract_id`, `tx_function_name`. Off by default.
	 *  Avoids a `/v1/index/transactions` call per event; for `print` events it's
	 *  the only source of the submitting sender. */
	txContext?: boolean;
	/**
	 * Return only these columns. The server projects the SELECT, so an
	 * unrequested field is physically absent — and the returned row type
	 * narrows to match, making a read of one a compile error rather than
	 * `undefined`.
	 *
	 * `cursor`, `block_height`, and `event_type` come back regardless: the
	 * first two are the consume contract, the third carries the discriminant.
	 * Don't list them.
	 *
	 * Cost note: this does NOT change your bill — Index meters per row read,
	 * not per field. What it buys is wire bytes, plus skipping the `blocks`
	 * join when `block_time` is omitted.
	 */
	fields?: readonly (keyof IndexEventOf<T> & string)[];
};

export type EventsWalkParams<T extends IndexEventType = IndexEventType> = Omit<
	EventsListParams<T>,
	"limit"
> &
	WalkOptions;

export type EventsConsumeParams<
	T extends IndexEventType = IndexEventType,
	TTx = never,
	// `fields` is omitted deliberately: the consume loop builds its page
	// request field-by-field and never forwarded it, so accepting the option
	// meant it type-checked and was then silently dropped. Consuming a
	// projected row is a real want — it needs the loop to thread it AND the
	// handler's row type to narrow, which is a larger change than pretending
	// the option works.
> = Omit<EventsListParams<T>, "cursor" | "fromCursor" | "limit" | "fields"> &
	IndexConsumeOptions<IndexEventOf<T>, EventsEnvelope<T>, TTx>;

// ── Contract calls (/v1/index/contract-calls) ──────────────────────

export type IndexContractCall = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	tx_id: string;
	tx_index: number;
	contract_id: string;
	function_name: string;
	sender: string;
	status: string;
	args: unknown[];
	result: unknown;
	result_hex: string | null;
};

export type ContractCallsEnvelope = {
	contract_calls: IndexContractCall[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
};

export type ContractCallsListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	/** One id, or several to scope the sweep to a set of contracts (max 20).
	 *  Mutually exclusive with `trait`. */
	contractId?: string | readonly string[];
	functionName?: string;
	sender?: string;
	fromHeight?: number;
	toHeight?: number;
	/** Restrict to contracts conforming to a trait/standard (e.g. "sip-010").
	 *  Mutually exclusive with contractId. */
	trait?: string;
};

export type ContractCallsWalkParams = Omit<ContractCallsListParams, "limit"> &
	WalkOptions;

export type ContractCallsConsumeParams<TTx = never> = Omit<
	ContractCallsListParams,
	"cursor" | "fromCursor" | "limit"
> &
	IndexConsumeOptions<IndexContractCall, ContractCallsEnvelope, TTx>;

// ── Canonical block-hash map (/v1/index/canonical) ─────────────────

/** One canonical block in the sync map. Lean by design — block + parent hash
 *  for chain linkage, burn anchor for Bitcoin confirmations. Use `blocks` for
 *  the full block resource. */
export type IndexCanonicalBlock = {
	cursor: string;
	block_height: number;
	block_hash: string;
	parent_hash: string;
	burn_block_height: number;
	burn_block_hash: string | null;
};

export type CanonicalEnvelope = {
	canonical: IndexCanonicalBlock[];
	next_cursor: string | null;
	tip: IndexTip;
};

export type CanonicalListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	fromHeight?: number;
	toHeight?: number;
};

export type CanonicalWalkParams = Omit<CanonicalListParams, "limit"> &
	WalkOptions;

// ── Blocks (/v1/index/blocks) ──────────────────────────────────────

/** A block resource. Metadata is intentionally thin — only chain-linkage and
 *  burn-anchor fields are persisted (no miner / tx_count / signer). */
export type IndexBlock = {
	cursor: string;
	block_height: number;
	block_hash: string;
	parent_hash: string;
	burn_block_height: number;
	burn_block_hash: string | null;
	block_time: string | null;
	canonical: boolean;
};

export type BlocksEnvelope = {
	blocks: IndexBlock[];
	next_cursor: string | null;
	tip: IndexTip;
};

export type BlockEnvelope = {
	block: IndexBlock;
	tip: IndexTip;
};

export type BlocksListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	fromHeight?: number;
	toHeight?: number;
};

export type BlocksWalkParams = Omit<BlocksListParams, "limit"> & WalkOptions;

// ── Transactions (/v1/index/transactions) ──────────────────────────

// Mirrors DecodedPostCondition in packages/api/src/index/transaction-decode.ts
// (source of truth) — this type is hand-maintained, not generated, so the two
// must move together.
export type IndexPostCondition =
	| {
			type: "stx";
			principal: string;
			condition_code: number;
			condition_code_name: string | null;
			amount: string;
	  }
	| {
			type: "ft";
			principal: string;
			asset_identifier: string;
			condition_code: number;
			condition_code_name: string | null;
			amount: string;
	  }
	| {
			type: "nft";
			principal: string;
			asset_identifier: string;
			asset_value: unknown;
			condition_code: number;
			condition_code_name: string | null;
	  }
	// SIP-045 (epoch 4.0 / pox-5): gates stake / register-for-bond / stake-update.
	| {
			type: "staking";
			principal: string;
			condition_code: number;
			condition_code_name: string | null;
			amount: string;
	  }
	// SIP-045 (epoch 4.0 / pox-5): gates non-locking PoX state changes, e.g.
	// unstake / announce-l1-early-exit. No amount — see PoxPostConditionWire.
	| {
			type: "pox";
			principal: string;
			condition_code: number;
			condition_code_name: string | null;
	  };

/** Full transaction document: columnar fields plus `raw_tx`-decoded enrichment.
 *  Payload sub-objects are present only for the matching `tx_type`; enrichment
 *  fields are null when `raw_tx` isn't decodable (e.g. burnchain ops). */
export type IndexTransaction = {
	cursor: string;
	tx_id: string;
	block_height: number;
	block_time?: string | null;
	tx_index: number;
	tx_type: string;
	sender: string;
	status: string;
	fee: string | null;
	nonce: string | null;
	sponsored: boolean | null;
	anchor_mode: string | null;
	post_condition_mode: string | null;
	post_conditions: IndexPostCondition[];
	contract_call?: {
		contract_id: string;
		function_name: string;
		function_args: unknown[];
		/** Raw hex-encoded ClarityValues; decode(function_args_hex[i]) === function_args[i]. */
		function_args_hex: string[];
		result: unknown;
		result_hex: string | null;
	};
	token_transfer?: { recipient: string; amount: string; memo: string };
	smart_contract?: {
		contract_id: string | null;
		clarity_version: number | null;
	};
	coinbase?: { alt_recipient: string | null };
	tenure_change?: { cause: number };
};

export type TransactionsEnvelope<TRow = IndexTransaction> = {
	transactions: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
};

export type TransactionEnvelope = {
	transaction: IndexTransaction;
	tip: IndexTip;
};

/** Transaction columns that survive any projection. */
export type TransactionAlwaysFields = "cursor" | "block_height" | "tx_id";

/** A Transaction row narrowed to the requested columns plus the always-present ones. */
export type TransactionFields<F extends keyof IndexTransaction & string> = Pick<
	IndexTransaction,
	(F | TransactionAlwaysFields) & keyof IndexTransaction
>;

export type TransactionsListParams = {
	/**
	 * Columns to return. The server projects the row, so an unrequested column
	 * is physically absent — and the narrowing overload on `list` makes reading
	 * one a compile error rather than `undefined` at runtime.
	 */
	fields?: readonly (keyof IndexTransaction & string)[];
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	type?: string;
	sender?: string;
	contractId?: string;
	fromHeight?: number;
	toHeight?: number;
};

export type TransactionsWalkParams = Omit<TransactionsListParams, "limit"> &
	WalkOptions;

// ── Stacking (/v1/index/stacking) ──────────────────────────────────

/** A decoded PoX-4 stacking action (one per stacking contract call). */
export type IndexStackingAction = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	burn_block_height: number;
	tx_id: string;
	tx_index: number;
	function_name: string;
	caller: string;
	stacker: string | null;
	delegate_to: string | null;
	amount_ustx: string | null;
	lock_period: number | null;
	pox_addr: {
		version: number | null;
		hashbytes: string | null;
		btc: string | null;
	};
	start_cycle: number | null;
	end_cycle: number | null;
	reward_cycle: number | null;
	signer_key: string | null;
	result_ok: boolean;
};

export type StackingEnvelope = {
	stacking: IndexStackingAction[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
	/** Present only when the PoX-4 decoder is disabled, explaining an empty feed. */
	notes?: string;
};

export type StackingListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	functionName?: string;
	stacker?: string;
	caller?: string;
	fromHeight?: number;
	toHeight?: number;
};

export type StackingWalkParams = Omit<StackingListParams, "limit"> &
	WalkOptions;

// ── Mempool (/v1/index/mempool) ────────────────────────────────────

/** A pending (unconfirmed) transaction. Like a transaction document but
 *  pre-chain — no block_height/tx_index/result/events — with `received_at` and
 *  a sequence cursor instead of a block position. */
export type IndexMempoolTransaction = {
	cursor: string;
	tx_id: string;
	tx_type: string;
	sender: string;
	received_at?: string | null;
	fee: string | null;
	nonce: string | null;
	sponsored: boolean | null;
	anchor_mode: string | null;
	post_condition_mode: string | null;
	post_conditions: IndexPostCondition[];
	contract_call?: {
		contract_id: string;
		function_name: string;
		function_args: unknown[];
	};
	token_transfer?: { recipient: string; amount: string; memo: string };
	smart_contract?: { clarity_version: number | null };
	coinbase?: { alt_recipient: string | null };
	tenure_change?: { cause: number };
};

export type MempoolEnvelope = {
	mempool: IndexMempoolTransaction[];
	next_cursor: string | null;
	tip: IndexTip;
};

export type MempoolTransactionEnvelope = {
	transaction: IndexMempoolTransaction;
	tip: IndexTip;
};

export type MempoolListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	sender?: string;
	type?: string;
	/** Filter to pending calls to a single contract (e.g. `SP….contract`). */
	contractId?: string;
	/** Filter to pending calls to a single function (composes with `contractId`). */
	functionName?: string;
};

export type MempoolWalkParams = Omit<MempoolListParams, "limit"> & WalkOptions;

// ── Print schema (/v1/index/contracts/:contract_id/print-schema) ───

/**
 * Empirical per-topic print payload schema for a contract, inferred from
 * sampled on-chain events. `topics` is sorted by count desc; `sampled` is true
 * when the contract has more print events than the windows examined.
 */
export type PrintSchemaResponse = {
	contract_id: string;
	topics: InferredTopicSchema[];
	sampled: boolean;
	total_events: number;
	/** True when the count hit the server-side cap (total_events is the cap). */
	total_events_capped: boolean;
	sample: {
		size: number;
		newest_height: number | null;
		oldest_height: number | null;
	};
	tip: IndexTip;
};

// ── sBTC peg (/v1/index/sbtc) ──────────────────────────────────────

/** The decoded `sbtc_events` topics — one source of truth in
 *  `@secondlayer/stacks/sbtc` (already a dependency), re-exported here. */
export type { SbtcEventTopic } from "@secondlayer/stacks/sbtc";

export type SbtcWithdrawalStatus = "REQUESTED" | "ACCEPTED" | "REJECTED";

/** A completed sBTC peg-in, keyed by `bitcoin_txid` (deposits carry no
 *  `request_id`). One terminal `completed-deposit` event per deposit. */
export type IndexSbtcDeposit = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	tx_id: string;
	tx_index: number;
	event_index: number;
	amount: string | null;
	sender: string | null;
	bitcoin_txid: string | null;
	output_index: number | null;
	recipient_btc_version: number | null;
	recipient_btc_hashbytes: string | null;
};

/** A deposit fetched by Bitcoin txid — always terminal, hence `status`. */
export type IndexSbtcDepositDetail = IndexSbtcDeposit & { status: "COMPLETED" };

/** A peg-out collapsed to one row per `request_id`, with lifecycle `status`
 *  derived from the latest accept/reject. */
export type IndexSbtcWithdrawal = {
	cursor: string;
	request_id: number;
	status: SbtcWithdrawalStatus;
	amount: string | null;
	sender: string | null;
	recipient_btc_version: number | null;
	recipient_btc_hashbytes: string | null;
	sweep_txid: string | null;
	/** BTC L1 settlement of the committed sweep: true once confirmed, false while
	 *  pending, null when there is no sweep yet (REQUESTED). */
	settlement_confirmed: boolean | null;
	/** BTC confirmations on the sweep tx, null when no settlement row yet. */
	btc_confirmations: number | null;
	/** Confirming BTC block height, null while unconfirmed / no sweep. */
	btc_block_height: number | null;
	/** When the sweep crossed the confirmation threshold (ISO), null otherwise. */
	confirmed_at: string | null;
	requested_at?: string | null;
	resolved_at?: string | null;
};

/** One phase of a withdrawal's lifecycle (the on-Stacks event that drove it). */
export type IndexSbtcWithdrawalPhase = {
	block_height: number;
	block_time: string | null;
	tx_id: string;
};

/** A single peg-out's full assembled lifecycle, fetched by `request_id`.
 *  `finalized` is true once terminal and past the reorg margin. */
export type IndexSbtcWithdrawalDetail = {
	request_id: number;
	status: SbtcWithdrawalStatus;
	amount: string | null;
	sender: string | null;
	recipient_btc_version: number | null;
	recipient_btc_hashbytes: string | null;
	requested: IndexSbtcWithdrawalPhase;
	accepted:
		| (IndexSbtcWithdrawalPhase & {
				sweep_txid: string | null;
				signer_bitmap: string | null;
		  })
		| null;
	rejected: IndexSbtcWithdrawalPhase | null;
	settlement: {
		sweep_txid: string | null;
		btc_confirmations: number | null;
		settlement_confirmed: boolean | null;
		/** Confirming Bitcoin block height; null until the sweep confirms. */
		btc_block_height: number | null;
		/** ISO timestamp the sweep first crossed the confirmation threshold. */
		confirmed_at: string | null;
	};
	finalized: boolean;
};

/** A raw decoded sBTC protocol-state event — the full `sbtc_events` row, one
 *  per event across all topics. */
export type IndexSbtcEvent = {
	cursor: string;
	block_height: number;
	block_time?: string | null;
	tx_id: string;
	tx_index: number;
	event_index: number;
	topic: SbtcEventTopic;
	request_id: number | null;
	amount: string | null;
	sender: string | null;
	recipient_btc_version: number | null;
	recipient_btc_hashbytes: string | null;
	bitcoin_txid: string | null;
	output_index: number | null;
	sweep_txid: string | null;
	burn_hash: string | null;
	burn_height: number | null;
	signer_bitmap: string | null;
	max_fee: string | null;
	fee: string | null;
	governance_contract_type: number | null;
	governance_new_contract: string | null;
	signer_aggregate_pubkey: string | null;
	signer_threshold: number | null;
	signer_address: string | null;
	signer_keys_count: number | null;
};

/** The peg "scoreboard" — a single all-time canonical aggregate. */
export type IndexSbtcSummary = {
	total_deposits: number;
	total_withdrawals_requested: number;
	total_withdrawals_accepted: number;
	total_withdrawals_rejected: number;
	net_peg_flow_sats: string;
	total_locked_sats: string;
	sbtc_supply_sats: string | null;
};

export type SbtcDepositsEnvelope<TRow = IndexSbtcDeposit> = {
	deposits: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	// Chain reorgs overlapping this page's height range; empty when none.
	reorgs: IndexReorg[];
	/** Present only when the sBTC decoder is disabled, explaining an empty feed. */
	notes?: string;
};

export type SbtcDepositEnvelope = {
	deposit: IndexSbtcDepositDetail;
	tip: IndexTip;
};

export type SbtcWithdrawalsEnvelope<TRow = IndexSbtcWithdrawal> = {
	withdrawals: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	reorgs: IndexReorg[];
	notes?: string;
};

export type SbtcWithdrawalEnvelope = {
	withdrawal: IndexSbtcWithdrawalDetail;
	tip: IndexTip;
};

export type SbtcEventsEnvelope = {
	events: IndexSbtcEvent[];
	next_cursor: string | null;
	tip: IndexTip;
	reorgs: IndexReorg[];
	notes?: string;
};

export type SbtcSummaryEnvelope = {
	summary: IndexSbtcSummary;
	tip: IndexTip;
	notes?: string;
};

export type SbtcDepositsListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	/** Clamp to the finality boundary — only settled deposits past the reorg margin. */
	confirmed?: boolean;
	sender?: string;
	bitcoinTxid?: string;
	fromHeight?: number;
	toHeight?: number;
	/**
	 * Columns to return. The server projects the row, so an unrequested column
	 * is physically absent — and the narrowing overload on `list` makes reading
	 * one a compile error rather than `undefined` at runtime.
	 */
	fields?: readonly (keyof IndexSbtcDeposit & string)[];
};

/** Deposit columns that survive any projection — pagination needs them. */
export type SbtcDepositAlwaysFields = "cursor" | "block_height";

/** A deposit row narrowed to the requested columns plus the always-present ones. */
export type SbtcDepositFields<F extends keyof IndexSbtcDeposit & string> = Pick<
	IndexSbtcDeposit,
	(F | SbtcDepositAlwaysFields) & keyof IndexSbtcDeposit
>;

export type SbtcDepositsWalkParams = Omit<SbtcDepositsListParams, "limit"> &
	WalkOptions;

/** SbtcWithdrawal columns that survive any projection. */
export type SbtcWithdrawalAlwaysFields = "cursor" | "request_id";

/** A SbtcWithdrawal row narrowed to the requested columns plus the always-present ones. */
export type SbtcWithdrawalFields<F extends keyof IndexSbtcWithdrawal & string> =
	Pick<
		IndexSbtcWithdrawal,
		(F | SbtcWithdrawalAlwaysFields) & keyof IndexSbtcWithdrawal
	>;

export type SbtcWithdrawalsListParams = {
	/**
	 * Columns to return. The server projects the row, so an unrequested column
	 * is physically absent — and the narrowing overload on `list` makes reading
	 * one a compile error rather than `undefined` at runtime.
	 */
	fields?: readonly (keyof IndexSbtcWithdrawal & string)[];
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	confirmed?: boolean;
	status?: SbtcWithdrawalStatus;
	sender?: string;
	requestId?: number;
	/** Filter by BTC L1 settlement: true → only confirmed sweeps; false → not
	 *  yet confirmed (pending or no sweep). */
	settlementConfirmed?: boolean;
	fromHeight?: number;
	toHeight?: number;
};

export type SbtcWithdrawalsWalkParams = Omit<
	SbtcWithdrawalsListParams,
	"limit"
> &
	WalkOptions;

export type SbtcEventsListParams = {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	confirmed?: boolean;
	topic?: SbtcEventTopic;
	sender?: string;
	requestId?: number;
	bitcoinTxid?: string;
	fromHeight?: number;
	toHeight?: number;
};

export type SbtcEventsWalkParams = Omit<SbtcEventsListParams, "limit"> &
	WalkOptions;

export type SbtcEventsConsumeParams<TTx = never> = Omit<
	SbtcEventsListParams,
	"cursor" | "fromCursor" | "limit"
> &
	IndexConsumeOptions<IndexSbtcEvent, SbtcEventsEnvelope, TTx>;

export type SbtcDepositsConsumeParams<TTx = never> = Omit<
	SbtcDepositsListParams,
	// `fields` omitted for the same reason as EventsConsumeParams: the consume
	// loop never forwarded it, so accepting the option meant it type-checked
	// and was then silently dropped. Consuming a projected row needs the
	// handler's row type to narrow too — a larger change than pretending the
	// option works.
	"cursor" | "fromCursor" | "limit" | "fields"
> &
	IndexConsumeOptions<IndexSbtcDeposit, SbtcDepositsEnvelope, TTx>;

/** `index.sbtc` — the decoded sBTC peg surface (deposits, withdrawals, raw
 *  events, scoreboard). The only productized decoded sBTC peg feed on Stacks. */
export interface SbtcResource {
	deposits: {
		/** Narrowing overload: `fields` shrinks the row type to exactly the
		 *  requested columns plus {@link SbtcDepositAlwaysFields}. */
		list<const F extends keyof IndexSbtcDeposit & string>(
			params: SbtcDepositsListParams & { fields: readonly F[] },
		): Promise<SbtcDepositsEnvelope<SbtcDepositFields<F>>>;
		list(params?: SbtcDepositsListParams): Promise<SbtcDepositsEnvelope>;
		walk(params?: SbtcDepositsWalkParams): AsyncIterable<IndexSbtcDeposit>;
		/** Checkpointed sweep of completed deposits — append-only, so safe to
		 *  mirror. See {@link IndexConsumeOptions}. */
		consume<TTx = never>(
			params: SbtcDepositsConsumeParams<TTx> & { sink?: ConsumerSink<TTx> },
		): Promise<{
			cursor: string | null;
			pages: number;
			emptyPolls: number;
		}>;
		/** Fetch a completed deposit by Bitcoin txid; 404 → null. */
		get(bitcoinTxid: string): Promise<SbtcDepositEnvelope | null>;
	};
	withdrawals: {
		/** Narrowing overload: `fields` shrinks the row type to exactly the
		 *  requested columns plus {@link SbtcWithdrawalAlwaysFields}. */
		list<const F extends keyof IndexSbtcWithdrawal & string>(
			params: SbtcWithdrawalsListParams & { fields: readonly F[] },
		): Promise<SbtcWithdrawalsEnvelope<SbtcWithdrawalFields<F>>>;
		list(params?: SbtcWithdrawalsListParams): Promise<SbtcWithdrawalsEnvelope>;
		walk(
			params?: SbtcWithdrawalsWalkParams,
		): AsyncIterable<IndexSbtcWithdrawal>;
		/** Fetch a withdrawal's full lifecycle by request id; 404 → null. */
		get(requestId: number): Promise<SbtcWithdrawalEnvelope | null>;
		// No `consume` by design: a withdrawal row is a lifecycle aggregate keyed
		// by `request_id`, and it MUTATES as the peg-out moves REQUESTED →
		// ACCEPTED/REJECTED → settled. A checkpointed cursor only ever moves
		// forward, so a consumer would commit a row as REQUESTED and never see it
		// transition. Mirror the append-only `events` feed and derive status from
		// the withdrawal-{create,accept,reject} topics instead.
	};
	events: {
		list(params?: SbtcEventsListParams): Promise<SbtcEventsEnvelope>;
		walk(params?: SbtcEventsWalkParams): AsyncIterable<IndexSbtcEvent>;
		/** Checkpointed sweep of the raw peg event log — append-only across all
		 *  six topics. See {@link IndexConsumeOptions}. */
		consume<TTx = never>(
			params: SbtcEventsConsumeParams<TTx> & { sink?: ConsumerSink<TTx> },
		): Promise<{
			cursor: string | null;
			pages: number;
			emptyPolls: number;
		}>;
	};
	/** The peg scoreboard — a single all-time aggregate (no pagination). */
	summary(): Promise<SbtcSummaryEnvelope>;
}

// ── PoX reward cycles (/v1/index/pox/cycles) ───────────────────────

/** Per-function action count within a reward cycle. */
export type IndexPoxFunctionCount = {
	function_name: string;
	count: number;
};

/** Aggregate stats for one PoX reward cycle — distinct from `index.stacking`
 *  (decoded per-call PoX-4 actions); this is the reward-cycle rollup. */
export type IndexPoxCycle = {
	reward_cycle: number;
	/** Total ustx locked across all stack-* calls in this cycle (bigint-safe string). */
	total_stacked_ustx: string;
	unique_stackers: number;
	unique_delegators: number;
	action_count: number;
	start_block_height: number;
	end_block_height: number;
	/** True when this is the latest reward cycle (still accumulating new actions). */
	is_current: boolean;
	function_breakdown: IndexPoxFunctionCount[];
};

export type PoxCyclesEnvelope = {
	cycles: IndexPoxCycle[];
	/** The next `reward_cycle` to page from (cycles descend), or null at the end. */
	next_cursor: number | null;
	tip: IndexTip;
	/** Present only when the PoX-4 decoder is disabled, explaining an empty feed. */
	notes?: string;
};

export type PoxCycleEnvelope = {
	cycle: IndexPoxCycle;
	tip: IndexTip;
	notes?: string;
};

export type PoxCyclesListParams = {
	/** A `reward_cycle` to page from; the page returns cycles below it (descending). */
	cursor?: number | null;
	limit?: number;
};

export type PoxCyclesWalkParams = Omit<PoxCyclesListParams, "limit"> &
	WalkOptions;

/** `index.pox` — PoX reward-cycle aggregates. */
export interface PoxResource {
	cycles: {
		list(params?: PoxCyclesListParams): Promise<PoxCyclesEnvelope>;
		walk(params?: PoxCyclesWalkParams): AsyncIterable<IndexPoxCycle>;
		/** Fetch one reward cycle's aggregate by number; 404 → null. */
		get(rewardCycle: number): Promise<PoxCycleEnvelope | null>;
	};
}

// ── PoX-5 events (/v1/index/pox5/events) ───────────────────────────

/** The decoded `pox-5` boot-contract print topics (SIP-045 Bitcoin Staking) —
 *  one source of truth in `@secondlayer/stacks/pox5` (already a dependency;
 *  the old "SDK owns its own dependency surface" rationale predated that). */
export type IndexPox5EventTopic = Pox5EventTopic;

/** A raw decoded PoX-5 print event. Promoted fields cover the hot query paths;
 *  `data` always carries the full decoded tuple, including nested shapes
 *  (`btc-lockup`, `bond-rewards`, `bond-periods`) that have no flat column. */
export type IndexPox5Event = {
	cursor: string;
	block_height: number;
	block_time: string | null;
	tx_id: string;
	tx_index: number;
	event_index: number;
	topic: IndexPox5EventTopic;
	staker: string | null;
	signer: string | null;
	signer_manager: string | null;
	bond_index: number | null;
	/** ustx, bigint-safe string. */
	amount_ustx: string | null;
	/** sats, bigint-safe string. */
	amount_sats: string | null;
	reward_cycle: number | null;
	first_reward_cycle: number | null;
	unlock_cycle: number | null;
	unlock_burn_height: number | null;
	is_l1_lock: boolean | null;
	signer_key: string | null;
	/** The full decoded print tuple as JSON. */
	data: unknown;
};

export type Pox5EventsEnvelope<TRow = IndexPox5Event> = {
	events: TRow[];
	next_cursor: string | null;
	tip: IndexTip;
	reorgs: IndexReorg[];
	/** Present only when the PoX-5 decoder is disabled, explaining an empty feed. */
	notes?: string;
};

/** Pox5Event columns that survive any projection. */
export type Pox5EventAlwaysFields = "cursor" | "block_height" | "topic";

/** A Pox5Event row narrowed to the requested columns plus the always-present ones. */
export type Pox5EventFields<F extends keyof IndexPox5Event & string> = Pick<
	IndexPox5Event,
	(F | Pox5EventAlwaysFields) & keyof IndexPox5Event
>;

export type Pox5EventsListParams = {
	/**
	 * Columns to return. The server projects the row, so an unrequested column
	 * is physically absent — and the narrowing overload on `list` makes reading
	 * one a compile error rather than `undefined` at runtime.
	 */
	fields?: readonly (keyof IndexPox5Event & string)[];
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	/** Clamp to the finality boundary — only rows past the reorg margin. */
	confirmed?: boolean;
	topic?: IndexPox5EventTopic;
	staker?: string;
	signer?: string;
	signerManager?: string;
	bondIndex?: number;
	rewardCycle?: number;
	fromHeight?: number;
	toHeight?: number;
};

export type Pox5EventsWalkParams = Omit<Pox5EventsListParams, "limit"> &
	WalkOptions;

/** `index.pox5` — decoded PoX-5 print events, the staking primitive from the
 *  epoch 4.0 hard fork onward (PoX-4's `index.stacking` stream ends there). */
export interface Pox5Resource {
	events: {
		/** Narrowing overload: `fields` shrinks the row type to exactly the
		 *  requested columns plus {@link Pox5EventAlwaysFields}. */
		list<const F extends keyof IndexPox5Event & string>(
			params: Pox5EventsListParams & { fields: readonly F[] },
		): Promise<Pox5EventsEnvelope<Pox5EventFields<F>>>;
		list(params?: Pox5EventsListParams): Promise<Pox5EventsEnvelope>;
		walk(params?: Pox5EventsWalkParams): AsyncIterable<IndexPox5Event>;
	};
}

/**
 * A consume call's feed filters: everything except the loop's own starting
 * checkpoint. `fromCursor` MUST be dropped — it is where the loop begins, not
 * a filter, and the API rejects `cursor` + `from_cursor` together, so
 * forwarding it would 400 every page after the first. `fromHeight`/`limit`
 * also appear in both, and the explicit page args at each call site override
 * them. The loop's non-wire options (onBatch, sink, signal, …) ride along
 * unread — `buildQuery` only reads the keys each list method names.
 */
function feedFilters<T extends { fromCursor?: string | null }>(params: T) {
	const { fromCursor: _checkpoint, ...filters } = params;
	return filters;
}

function firstWalkFromHeight(params: {
	cursor?: string | number | null;
	fromCursor?: string | null;
	fromHeight?: number;
}): number | undefined {
	if (params.fromHeight !== undefined) return params.fromHeight;
	if (params.cursor || params.fromCursor) return undefined;
	return 0;
}

/** The query params every Index keyset feed shares: page window, height range,
 *  column projection. Keeping the camel→snake mapping in one place means a new
 *  feed can't typo `from_cursor` into a silently-dropped filter. `buildQuery`
 *  drops undefined/null and comma-joins arrays, so a feed whose params type has
 *  no height window or `fields` contributes nothing for those keys. PoX cycles
 *  page by a numeric cursor and opt out. */
function indexPageQuery(params: {
	cursor?: string | null;
	fromCursor?: string | null;
	limit?: number;
	fromHeight?: number;
	toHeight?: number;
	fields?: readonly string[];
}) {
	return {
		cursor: params.cursor,
		from_cursor: params.fromCursor,
		limit: params.limit,
		from_height: params.fromHeight,
		to_height: params.toHeight,
		fields: params.fields,
	};
}

/** Pagination overrides `keysetWalk` applies to each page request; the original
 *  filter params are merged in by the per-resource list closure. */
type PageParams<C = string> = {
	limit: number;
	cursor?: C | null;
	fromCursor?: string | null;
	fromHeight?: number;
	signal?: AbortSignal;
};

/**
 * `index.ftTransfers` — callable shorthand for `.list()`, with `.list`/`.walk`
 * still available: `await sl.index.ftTransfers({ contractId })`.
 *
 * The API accepts `contract_id`/`sender`/`recipient` equality filters only —
 * no amount filtering and no asset-slug resolution on /v1/index/ft-transfers.
 */
export interface FtTransfersResource {
	/** Narrowing overload for the callable shorthand — same wire path as
	 *  `list`, so it must narrow identically. */
	<const F extends keyof FtTransfer & string>(
		params: FtTransfersListParams & { fields: readonly F[] },
	): Promise<FtTransfersEnvelope<FtTransferFields<F>>>;
	(params?: FtTransfersListParams): Promise<FtTransfersEnvelope>;
	/** Narrowing overload: `fields` shrinks the row type to exactly the
	 *  requested columns plus {@link FtTransferAlwaysFields}. */
	list<const F extends keyof FtTransfer & string>(
		params: FtTransfersListParams & { fields: readonly F[] },
	): Promise<FtTransfersEnvelope<FtTransferFields<F>>>;
	list(params?: FtTransfersListParams): Promise<FtTransfersEnvelope>;
	/** Narrowing overload, matching `list`: `walk` forwards `fields` to the
	 *  wire, so without this the yielded rows are stripped while the type
	 *  still promises every column. */
	walk<const F extends keyof FtTransfer & string>(
		params: FtTransfersWalkParams & { fields: readonly F[] },
	): AsyncIterable<FtTransferFields<F>>;
	walk(params?: FtTransfersWalkParams): AsyncIterable<FtTransfer>;
}

/** `index.nftTransfers` — callable shorthand for `.list()` (see {@link FtTransfersResource}). */
export interface NftTransfersResource {
	/** Narrowing overload for the callable shorthand — same wire path as
	 *  `list`, so it must narrow identically. */
	<const F extends keyof NftTransfer & string>(
		params: NftTransfersListParams & { fields: readonly F[] },
	): Promise<NftTransfersEnvelope<NftTransferFields<F>>>;
	(params?: NftTransfersListParams): Promise<NftTransfersEnvelope>;
	/** Narrowing overload: `fields` shrinks the row type to exactly the
	 *  requested columns plus {@link NftTransferAlwaysFields}. */
	list<const F extends keyof NftTransfer & string>(
		params: NftTransfersListParams & { fields: readonly F[] },
	): Promise<NftTransfersEnvelope<NftTransferFields<F>>>;
	list(params?: NftTransfersListParams): Promise<NftTransfersEnvelope>;
	/** Narrowing overload, matching `list` (see {@link FtTransfersResource}). */
	walk<const F extends keyof NftTransfer & string>(
		params: NftTransfersWalkParams & { fields: readonly F[] },
	): AsyncIterable<NftTransferFields<F>>;
	walk(params?: NftTransfersWalkParams): AsyncIterable<NftTransfer>;
}

/** `index.events` — callable shorthand for `.list()`; `eventType` is required. */
export interface IndexEventsResource {
	/** Narrowing overload for the callable shorthand — same wire path as
	 *  `list`, so it must narrow identically. */
	<T extends IndexEventType, const F extends keyof IndexEventOf<T> & string>(
		params: EventsListParams<T> & { fields: readonly F[] },
	): Promise<EventsEnvelope<T, IndexEventFields<T, F>>>;
	<T extends IndexEventType>(
		params: EventsListParams<T>,
	): Promise<EventsEnvelope<T>>;
	/** Narrowing overload: `fields` shrinks the returned row type to exactly
	 *  the requested columns plus {@link IndexAlwaysFields}. `const F` means no
	 *  `as const` at the call site. */
	list<
		T extends IndexEventType,
		const F extends keyof IndexEventOf<T> & string,
	>(
		params: EventsListParams<T> & { fields: readonly F[] },
	): Promise<EventsEnvelope<T, IndexEventFields<T, F>>>;
	list<T extends IndexEventType>(
		params: EventsListParams<T>,
	): Promise<EventsEnvelope<T>>;
	/** Narrowing overload, matching `list`: `walk` forwards `fields` to the
	 *  wire, so without this the yielded rows are stripped while the type
	 *  still promises every column. */
	walk<
		T extends IndexEventType,
		const F extends keyof IndexEventOf<T> & string,
	>(
		params: EventsWalkParams<T> & { fields: readonly F[] },
	): AsyncIterable<IndexEventFields<T, F>>;
	walk<T extends IndexEventType>(
		params: EventsWalkParams<T>,
	): AsyncIterable<IndexEventOf<T>>;
	consume<T extends IndexEventType, TTx = never>(
		// The redundant `sink` member gives TS a DIRECT inference site for TTx —
		// inference does not reliably traverse the params alias intersection.
		params: EventsConsumeParams<T, TTx> & {
			sink?: ConsumerSink<TTx>;
		},
	): Promise<{ cursor: string | null; pages: number; emptyPolls: number }>;
}

/** Per-event-type filter vocabulary in the {@link IndexDiscovery} doc. */
export type IndexEventTypeFilters = {
	columns?: string[];
	allowed_filters?: string[];
	equality_filters?: string[];
	required_non_null?: string[];
};

/** The `GET /v1/index` discovery doc — live endpoint + filter vocabulary.
 *  Shape is intentionally open (the server may add fields); the agent-relevant
 *  parts are the per-type filter rules. */
export type IndexDiscovery = {
	event_type_filters?: Record<string, IndexEventTypeFilters>;
	[key: string]: unknown;
};

export class Index extends BaseClient {
	constructor(options: Partial<SecondLayerOptions> = {}) {
		super(options);
	}

	/**
	 * Index discovery doc — the live vocabulary: every endpoint, each event type's
	 * columns, allowed/equality filters, and required-non-null fields. Read this to
	 * learn what's queryable (and which types accept `trait`) instead of hardcoding.
	 */
	discover(): Promise<IndexDiscovery> {
		return this.request<IndexDiscovery>("GET", "/v1/index");
	}

	/**
	 * Empirical per-topic print payload schema for a contract — what topics it
	 * emits and each field's observed Clarity/TS/column types. Anonymous read;
	 * 404 → null.
	 */
	async printSchema(contractId: string): Promise<PrintSchemaResponse | null> {
		return this.requestOrNull<PrintSchemaResponse>(
			"GET",
			`/v1/index/contracts/${encodeURIComponent(contractId)}/print-schema`,
		);
	}

	/** Callable: `index.ftTransfers(params)` ≡ `index.ftTransfers.list(params)`. */
	readonly ftTransfers: FtTransfersResource = Object.assign(
		(params: FtTransfersListParams = {}): Promise<FtTransfersEnvelope> =>
			this.listFtTransfers(params),
		{
			list: (
				params: FtTransfersListParams = {},
			): Promise<FtTransfersEnvelope> => this.listFtTransfers(params),
			walk: (params: FtTransfersWalkParams = {}): AsyncIterable<FtTransfer> =>
				this.walkFtTransfers(params),
		},
	);

	/** Callable: `index.nftTransfers(params)` ≡ `index.nftTransfers.list(params)`. */
	readonly nftTransfers: NftTransfersResource = Object.assign(
		(params: NftTransfersListParams = {}): Promise<NftTransfersEnvelope> =>
			this.listNftTransfers(params),
		{
			list: (
				params: NftTransfersListParams = {},
			): Promise<NftTransfersEnvelope> => this.listNftTransfers(params),
			walk: (params: NftTransfersWalkParams = {}): AsyncIterable<NftTransfer> =>
				this.walkNftTransfers(params),
		},
	);

	/** Generic decoded events by `event_type` (the full /v1/index/events surface).
	 *  Callable: `index.events(params)` ≡ `index.events.list(params)`. */
	readonly events: IndexEventsResource = Object.assign(
		<T extends IndexEventType>(
			params: EventsListParams<T>,
		): Promise<EventsEnvelope<T>> => this.listEvents(params),
		{
			list: (<T extends IndexEventType>(
				params: EventsListParams<T>,
			): Promise<EventsEnvelope<T>> =>
				this.listEvents(params)) as IndexEventsResource["list"],
			walk: <T extends IndexEventType>(
				params: EventsWalkParams<T>,
			): AsyncIterable<IndexEventOf<T>> => this.walkEvents(params),
			consume: <T extends IndexEventType, TTx = never>(
				// The redundant `sink` member gives TS a DIRECT inference site for
				// TTx — inference does not traverse the Omit-intersection alias.
				params: EventsConsumeParams<T, TTx> & { sink?: ConsumerSink<TTx> },
			) =>
				consumeIndexFeed<IndexEventOf<T>, EventsEnvelope<T>, TTx>({
					...params,
					fetchPage: ({ cursor, fromHeight, limit }) =>
						this.listEvents({
							...feedFilters(params),
							cursor,
							fromHeight,
							limit,
							signal: params.signal,
						}),
					itemsOf: (envelope) => envelope.events,
				}),
		},
	);

	readonly contractCalls: {
		list: (params?: ContractCallsListParams) => Promise<ContractCallsEnvelope>;
		walk: (
			params?: ContractCallsWalkParams,
		) => AsyncIterable<IndexContractCall>;
		consume: <TTx = never>(
			params: ContractCallsConsumeParams<TTx> & { sink?: ConsumerSink<TTx> },
		) => Promise<{ cursor: string | null; pages: number; emptyPolls: number }>;
	} = {
		list: (
			params: ContractCallsListParams = {},
		): Promise<ContractCallsEnvelope> => this.listContractCalls(params),
		walk: (
			params: ContractCallsWalkParams = {},
		): AsyncIterable<IndexContractCall> => this.walkContractCalls(params),
		consume: <TTx = never>(
			params: ContractCallsConsumeParams<TTx> & { sink?: ConsumerSink<TTx> },
		) =>
			consumeIndexFeed<IndexContractCall, ContractCallsEnvelope, TTx>({
				...params,
				fetchPage: ({ cursor, fromHeight, limit }) =>
					this.listContractCalls({
						...feedFilters(params),
						cursor,
						fromHeight,
						limit,
						signal: params.signal,
					}),
				itemsOf: (envelope) => envelope.contract_calls,
			}),
	};

	/** Canonical block-hash map — sync only the current canonical chain. */
	readonly canonical: {
		list: (params?: CanonicalListParams) => Promise<CanonicalEnvelope>;
		walk: (params?: CanonicalWalkParams) => AsyncIterable<IndexCanonicalBlock>;
	} = {
		list: (params: CanonicalListParams = {}): Promise<CanonicalEnvelope> =>
			this.listCanonical(params),
		walk: (
			params: CanonicalWalkParams = {},
		): AsyncIterable<IndexCanonicalBlock> => this.walkCanonical(params),
	};

	/** Canonical blocks: paginated `list`/`walk`, plus `get` by height or hash
	 *  (resolves to null on 404). */
	readonly blocks: {
		list: (params?: BlocksListParams) => Promise<BlocksEnvelope>;
		walk: (params?: BlocksWalkParams) => AsyncIterable<IndexBlock>;
		get: (ref: string | number) => Promise<BlockEnvelope | null>;
	} = {
		list: (params: BlocksListParams = {}): Promise<BlocksEnvelope> =>
			this.listBlocks(params),
		walk: (params: BlocksWalkParams = {}): AsyncIterable<IndexBlock> =>
			this.walkBlocks(params),
		get: (ref: string | number): Promise<BlockEnvelope | null> =>
			this.getBlock(ref),
	};

	/** Full transaction documents: paginated `list`/`walk`, plus `get` by tx_id
	 *  (resolves to null on 404). */
	readonly transactions: {
		/** Narrowing overload: `fields` shrinks the row type to exactly the
		 *  requested columns plus {@link TransactionAlwaysFields}. Declared as
		 *  an overloaded call signature because an arrow property cannot carry
		 *  overloads. */
		list: {
			<const F extends keyof IndexTransaction & string>(
				params: TransactionsListParams & { fields: readonly F[] },
			): Promise<TransactionsEnvelope<TransactionFields<F>>>;
			(params?: TransactionsListParams): Promise<TransactionsEnvelope>;
		};
		walk: (params?: TransactionsWalkParams) => AsyncIterable<IndexTransaction>;
		get: (txId: string) => Promise<TransactionEnvelope | null>;
		getProof: (txId: string) => Promise<TransactionProof | null>;
	} = {
		list: (
			params: TransactionsListParams = {},
		): Promise<TransactionsEnvelope> => this.listTransactions(params),
		walk: (
			params: TransactionsWalkParams = {},
		): AsyncIterable<IndexTransaction> => this.walkTransactions(params),
		get: (txId: string): Promise<TransactionEnvelope | null> =>
			this.getTransaction(txId),
		getProof: (txId: string): Promise<TransactionProof | null> =>
			this.getTransactionProof(txId),
	};

	/** Decoded PoX-4 stacking actions. Empty (with a `notes` hint) when the
	 *  platform's PoX-4 decoder is disabled. */
	readonly stacking: {
		list: (params?: StackingListParams) => Promise<StackingEnvelope>;
		walk: (params?: StackingWalkParams) => AsyncIterable<IndexStackingAction>;
	} = {
		list: (params: StackingListParams = {}): Promise<StackingEnvelope> =>
			this.listStacking(params),
		walk: (
			params: StackingWalkParams = {},
		): AsyncIterable<IndexStackingAction> => this.walkStacking(params),
	};

	/** Pending (unconfirmed) transactions: paginated `list`/`walk`, plus `get` by
	 *  tx_id (resolves to null when the tx has confirmed or dropped). */
	readonly mempool: {
		list: (params?: MempoolListParams) => Promise<MempoolEnvelope>;
		walk: (
			params?: MempoolWalkParams,
		) => AsyncIterable<IndexMempoolTransaction>;
		get: (txId: string) => Promise<MempoolTransactionEnvelope | null>;
	} = {
		list: (params: MempoolListParams = {}): Promise<MempoolEnvelope> =>
			this.listMempool(params),
		walk: (
			params: MempoolWalkParams = {},
		): AsyncIterable<IndexMempoolTransaction> => this.walkMempool(params),
		get: (txId: string): Promise<MempoolTransactionEnvelope | null> =>
			this.getMempoolTx(txId),
	};

	/** Decoded sBTC peg surface — the only productized decoded sBTC peg feed on
	 *  Stacks. `deposits`/`withdrawals`/`events` paginate; `summary` is a scalar. */
	readonly sbtc: SbtcResource = {
		deposits: {
			list: (
				params: SbtcDepositsListParams = {},
			): Promise<SbtcDepositsEnvelope> => this.listSbtcDeposits(params),
			walk: (
				params: SbtcDepositsWalkParams = {},
			): AsyncIterable<IndexSbtcDeposit> => this.walkSbtcDeposits(params),
			consume: <TTx = never>(
				params: SbtcDepositsConsumeParams<TTx> & { sink?: ConsumerSink<TTx> },
			) =>
				consumeIndexFeed<IndexSbtcDeposit, SbtcDepositsEnvelope, TTx>({
					...params,
					fetchPage: ({ cursor, fromHeight, limit }) =>
						this.listSbtcDeposits({
							...feedFilters(params),
							cursor,
							fromHeight,
							limit,
							signal: params.signal,
						}),
					itemsOf: (envelope) => envelope.deposits,
				}),
			get: (bitcoinTxid: string): Promise<SbtcDepositEnvelope | null> =>
				this.getSbtcDeposit(bitcoinTxid),
		},
		withdrawals: {
			list: (
				params: SbtcWithdrawalsListParams = {},
			): Promise<SbtcWithdrawalsEnvelope> => this.listSbtcWithdrawals(params),
			walk: (
				params: SbtcWithdrawalsWalkParams = {},
			): AsyncIterable<IndexSbtcWithdrawal> => this.walkSbtcWithdrawals(params),
			get: (requestId: number): Promise<SbtcWithdrawalEnvelope | null> =>
				this.getSbtcWithdrawal(requestId),
		},
		events: {
			list: (params: SbtcEventsListParams = {}): Promise<SbtcEventsEnvelope> =>
				this.listSbtcEvents(params),
			walk: (
				params: SbtcEventsWalkParams = {},
			): AsyncIterable<IndexSbtcEvent> => this.walkSbtcEvents(params),
			consume: <TTx = never>(
				params: SbtcEventsConsumeParams<TTx> & { sink?: ConsumerSink<TTx> },
			) =>
				consumeIndexFeed<IndexSbtcEvent, SbtcEventsEnvelope, TTx>({
					...params,
					fetchPage: ({ cursor, fromHeight, limit }) =>
						this.listSbtcEvents({
							...feedFilters(params),
							cursor,
							fromHeight,
							limit,
							signal: params.signal,
						}),
					itemsOf: (envelope) => envelope.events,
				}),
		},
		summary: (): Promise<SbtcSummaryEnvelope> => this.getSbtcSummary(),
	};

	/** PoX reward-cycle aggregates. Distinct from `stacking` (per-call PoX-4
	 *  actions); `cycles` is the reward-cycle rollup. */
	readonly pox: PoxResource = {
		cycles: {
			list: (params: PoxCyclesListParams = {}): Promise<PoxCyclesEnvelope> =>
				this.listPoxCycles(params),
			walk: (params: PoxCyclesWalkParams = {}): AsyncIterable<IndexPoxCycle> =>
				this.walkPoxCycles(params),
			get: (rewardCycle: number): Promise<PoxCycleEnvelope | null> =>
				this.getPoxCycle(rewardCycle),
		},
	};

	/** Decoded PoX-5 print events (SIP-045 Bitcoin Staking) — the raw pox-5
	 *  boot-contract log, all 19 topics. Distinct from `stacking` (PoX-4 era). */
	readonly pox5: Pox5Resource = {
		events: {
			list: (params: Pox5EventsListParams = {}): Promise<Pox5EventsEnvelope> =>
				this.listPox5Events(params),
			walk: (
				params: Pox5EventsWalkParams = {},
			): AsyncIterable<IndexPox5Event> => this.walkPox5Events(params),
		},
	};

	private async listFtTransfers(
		params: FtTransfersListParams & RequestSignal = {},
	): Promise<FtTransfersEnvelope> {
		return this.request<FtTransfersEnvelope>(
			"GET",
			`/v1/index/ft-transfers${buildQuery({
				...indexPageQuery(params),
				contract_id: params.contractId,
				asset_identifier: params.assetIdentifier,
				sender: params.sender,
				recipient: params.recipient,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private async listNftTransfers(
		params: NftTransfersListParams & RequestSignal = {},
	): Promise<NftTransfersEnvelope> {
		return this.request<NftTransfersEnvelope>(
			"GET",
			`/v1/index/nft-transfers${buildQuery({
				...indexPageQuery(params),
				contract_id: params.contractId,
				asset_identifier: params.assetIdentifier,
				sender: params.sender,
				recipient: params.recipient,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	/** Shared keyset-pagination loop for every `walk*` feed: seed the cursor from
	 *  `cursor`/`fromCursor` (and `fromHeight` 0 on the first page), then page until
	 *  the server stops advancing the cursor (`next_cursor` null or unchanged). A
	 *  short page is not a tail signal: the server clamps oversized limits without
	 *  saying so, so trusting page length truncated backfills. The cost is one
	 *  extra empty fetch at the end of feeds that never return a null cursor.
	 *  `list` merges the resource's filter params; `itemsOf` selects the envelope's
	 *  item array. Each page fetch retries under `fetchPageWithRetry`; the cursor
	 *  is a string keyset for every feed except PoX cycles, which use a number. */
	private async *keysetWalk<
		E extends { next_cursor: C | null },
		T,
		C extends string | number = string,
	>(
		params: WalkOptions & {
			cursor?: C | null;
			fromCursor?: string | null;
			fromHeight?: number;
		},
		list: (page: PageParams<C>) => Promise<E>,
		itemsOf: (envelope: E) => T[],
	): AsyncGenerator<T> {
		const batchSize = params.batchSize ?? 200;
		if (
			!Number.isInteger(batchSize) ||
			batchSize < 1 ||
			batchSize > INDEX_MAX_PAGE_SIZE
		) {
			throw new ValidationError(
				`walk batchSize must be an integer from 1 to ${INDEX_MAX_PAGE_SIZE} (the largest page the Index serves); got ${batchSize}.`,
				400,
			);
		}
		// `fromCursor` is a string keyset on every feed that accepts it; PoX
		// cycles (numeric cursor) never pass one.
		let cursor: C | null =
			params.cursor ?? (params.fromCursor as C | null | undefined) ?? null;
		let firstPage = true;

		// Abort is a rejection at every boundary, never a clean return: a walk
		// that ends without throwing has reached the end of the feed.
		const throwIfAborted = () => {
			if (params.signal?.aborted) throw params.signal.reason ?? abortError();
		};

		for (;;) {
			throwIfAborted();
			const page: PageParams<C> = {
				limit: batchSize,
				cursor: firstPage ? params.cursor : cursor,
				fromCursor: firstPage ? params.fromCursor : undefined,
				fromHeight: firstPage ? firstWalkFromHeight(params) : undefined,
				signal: params.signal,
			};
			const envelope = await fetchPageWithRetry(() => list(page), {
				retryCount: params.retryCount,
				retryDelay: params.retryDelay,
				onError: params.onError,
				sleep: defaultSleep,
				signal: params.signal,
			});

			const items = itemsOf(envelope);
			for (const item of items) {
				throwIfAborted();
				yield item;
			}

			const nextCursor = envelope.next_cursor;
			if (nextCursor === null || nextCursor === undefined) return;
			if (nextCursor === cursor) return;

			cursor = nextCursor;
			firstPage = false;
		}
	}

	private walkFtTransfers(
		params: FtTransfersWalkParams = {},
	): AsyncGenerator<FtTransfer> {
		return this.keysetWalk(
			params,
			(page) => this.listFtTransfers({ ...params, ...page }),
			(e) => e.events,
		);
	}

	private walkNftTransfers(
		params: NftTransfersWalkParams = {},
	): AsyncGenerator<NftTransfer> {
		return this.keysetWalk(
			params,
			(page) => this.listNftTransfers({ ...params, ...page }),
			(e) => e.events,
		);
	}

	private async listEvents<T extends IndexEventType>(
		params: EventsListParams<T> & RequestSignal,
	): Promise<EventsEnvelope<T>> {
		return this.request<EventsEnvelope<T>>(
			"GET",
			`/v1/index/events${buildQuery({
				...indexPageQuery(params),
				event_type: params.eventType,
				contract_id: params.contractId,
				asset_identifier: params.assetIdentifier,
				sender: params.sender,
				recipient: params.recipient,
				trait: params.trait,
				tx_context: params.txContext ? "true" : undefined,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkEvents<T extends IndexEventType>(
		params: EventsWalkParams<T>,
	): AsyncGenerator<IndexEventOf<T>> {
		return this.keysetWalk(
			params,
			(page) => this.listEvents({ ...params, ...page }),
			(e) => e.events,
		);
	}

	private async listContractCalls(
		params: ContractCallsListParams & RequestSignal = {},
	): Promise<ContractCallsEnvelope> {
		return this.request<ContractCallsEnvelope>(
			"GET",
			`/v1/index/contract-calls${buildQuery({
				...indexPageQuery(params),
				contract_id: params.contractId,
				function_name: params.functionName,
				sender: params.sender,
				trait: params.trait,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkContractCalls(
		params: ContractCallsWalkParams = {},
	): AsyncGenerator<IndexContractCall> {
		return this.keysetWalk(
			params,
			(page) => this.listContractCalls({ ...params, ...page }),
			(e) => e.contract_calls,
		);
	}

	private async listCanonical(
		params: CanonicalListParams & RequestSignal = {},
	): Promise<CanonicalEnvelope> {
		return this.request<CanonicalEnvelope>(
			"GET",
			`/v1/index/canonical${buildQuery(indexPageQuery(params))}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkCanonical(
		params: CanonicalWalkParams = {},
	): AsyncGenerator<IndexCanonicalBlock> {
		return this.keysetWalk(
			params,
			(page) => this.listCanonical({ ...params, ...page }),
			(e) => e.canonical,
		);
	}

	private async listBlocks(
		params: BlocksListParams & RequestSignal = {},
	): Promise<BlocksEnvelope> {
		return this.request<BlocksEnvelope>(
			"GET",
			`/v1/index/blocks${buildQuery(indexPageQuery(params))}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private async getBlock(ref: string | number): Promise<BlockEnvelope | null> {
		return this.requestOrNull<BlockEnvelope>(
			"GET",
			`/v1/index/blocks/${encodeURIComponent(String(ref))}`,
		);
	}

	private walkBlocks(
		params: BlocksWalkParams = {},
	): AsyncGenerator<IndexBlock> {
		return this.keysetWalk(
			params,
			(page) => this.listBlocks({ ...params, ...page }),
			(e) => e.blocks,
		);
	}

	private async listTransactions(
		params: TransactionsListParams & RequestSignal = {},
	): Promise<TransactionsEnvelope> {
		return this.request<TransactionsEnvelope>(
			"GET",
			`/v1/index/transactions${buildQuery({
				...indexPageQuery(params),
				type: params.type,
				sender: params.sender,
				contract_id: params.contractId,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private async getTransaction(
		txId: string,
	): Promise<TransactionEnvelope | null> {
		return this.requestOrNull<TransactionEnvelope>(
			"GET",
			`/v1/index/transactions/${encodeURIComponent(txId)}`,
		);
	}

	/** Fetch the inclusion proof for a tx (raw tx + Nakamoto header + merkle path)
	 *  to verify client-side with `verifyTransactionProof`. 404 → null. A 503
	 *  (`PROOF_TX_SET_INCOMPLETE` / `PROOF_NODE_UNAVAILABLE`) surfaces as an
	 *  ApiError — the proof can't be assembled on this deployment right now. */
	private async getTransactionProof(
		txId: string,
	): Promise<TransactionProof | null> {
		return this.requestOrNull<TransactionProof>(
			"GET",
			`/v1/index/transactions/${encodeURIComponent(txId)}/proof`,
		);
	}

	private walkTransactions(
		params: TransactionsWalkParams = {},
	): AsyncGenerator<IndexTransaction> {
		return this.keysetWalk(
			params,
			(page) => this.listTransactions({ ...params, ...page }),
			(e) => e.transactions,
		);
	}

	private async listStacking(
		params: StackingListParams & RequestSignal = {},
	): Promise<StackingEnvelope> {
		return this.request<StackingEnvelope>(
			"GET",
			`/v1/index/stacking${buildQuery({
				...indexPageQuery(params),
				function_name: params.functionName,
				stacker: params.stacker,
				caller: params.caller,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkStacking(
		params: StackingWalkParams = {},
	): AsyncGenerator<IndexStackingAction> {
		return this.keysetWalk(
			params,
			(page) => this.listStacking({ ...params, ...page }),
			(e) => e.stacking,
		);
	}

	private async listMempool(
		params: MempoolListParams & RequestSignal = {},
	): Promise<MempoolEnvelope> {
		return this.request<MempoolEnvelope>(
			"GET",
			`/v1/index/mempool${buildQuery({
				...indexPageQuery(params),
				sender: params.sender,
				type: params.type,
				contract_id: params.contractId,
				function_name: params.functionName,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private async getMempoolTx(
		txId: string,
	): Promise<MempoolTransactionEnvelope | null> {
		return this.requestOrNull<MempoolTransactionEnvelope>(
			"GET",
			`/v1/index/mempool/${encodeURIComponent(txId)}`,
		);
	}

	private walkMempool(
		params: MempoolWalkParams = {},
	): AsyncGenerator<IndexMempoolTransaction> {
		// Mempool has no height filter; listMempool ignores the seeded fromHeight.
		return this.keysetWalk(
			params,
			(page) => this.listMempool({ ...params, ...page }),
			(e) => e.mempool,
		);
	}

	private async listSbtcDeposits(
		params: SbtcDepositsListParams & RequestSignal = {},
	): Promise<SbtcDepositsEnvelope> {
		return this.request<SbtcDepositsEnvelope>(
			"GET",
			`/v1/index/sbtc/deposits${buildQuery({
				...indexPageQuery(params),
				confirmed: params.confirmed,
				sender: params.sender,
				bitcoin_txid: params.bitcoinTxid,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkSbtcDeposits(
		params: SbtcDepositsWalkParams = {},
	): AsyncGenerator<IndexSbtcDeposit> {
		return this.keysetWalk(
			params,
			(page) => this.listSbtcDeposits({ ...params, ...page }),
			(e) => e.deposits,
		);
	}

	private async getSbtcDeposit(
		bitcoinTxid: string,
	): Promise<SbtcDepositEnvelope | null> {
		return this.requestOrNull<SbtcDepositEnvelope>(
			"GET",
			`/v1/index/sbtc/deposits/${encodeURIComponent(bitcoinTxid)}`,
		);
	}

	private async listSbtcWithdrawals(
		params: SbtcWithdrawalsListParams & RequestSignal = {},
	): Promise<SbtcWithdrawalsEnvelope> {
		return this.request<SbtcWithdrawalsEnvelope>(
			"GET",
			`/v1/index/sbtc/withdrawals${buildQuery({
				...indexPageQuery(params),
				confirmed: params.confirmed,
				status: params.status,
				sender: params.sender,
				request_id: params.requestId,
				settlement_confirmed: params.settlementConfirmed,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkSbtcWithdrawals(
		params: SbtcWithdrawalsWalkParams = {},
	): AsyncGenerator<IndexSbtcWithdrawal> {
		return this.keysetWalk(
			params,
			(page) => this.listSbtcWithdrawals({ ...params, ...page }),
			(e) => e.withdrawals,
		);
	}

	private async getSbtcWithdrawal(
		requestId: number,
	): Promise<SbtcWithdrawalEnvelope | null> {
		return this.requestOrNull<SbtcWithdrawalEnvelope>(
			"GET",
			`/v1/index/sbtc/withdrawals/${requestId}`,
		);
	}

	private async listSbtcEvents(
		params: SbtcEventsListParams & RequestSignal = {},
	): Promise<SbtcEventsEnvelope> {
		return this.request<SbtcEventsEnvelope>(
			"GET",
			`/v1/index/sbtc/events${buildQuery({
				...indexPageQuery(params),
				confirmed: params.confirmed,
				topic: params.topic,
				sender: params.sender,
				request_id: params.requestId,
				bitcoin_txid: params.bitcoinTxid,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkSbtcEvents(
		params: SbtcEventsWalkParams = {},
	): AsyncGenerator<IndexSbtcEvent> {
		return this.keysetWalk(
			params,
			(page) => this.listSbtcEvents({ ...params, ...page }),
			(e) => e.events,
		);
	}

	private async getSbtcSummary(): Promise<SbtcSummaryEnvelope> {
		return this.request<SbtcSummaryEnvelope>("GET", "/v1/index/sbtc/summary");
	}

	private async listPox5Events(
		params: Pox5EventsListParams & RequestSignal = {},
	): Promise<Pox5EventsEnvelope> {
		return this.request<Pox5EventsEnvelope>(
			"GET",
			`/v1/index/pox5/events${buildQuery({
				...indexPageQuery(params),
				confirmed: params.confirmed,
				topic: params.topic,
				staker: params.staker,
				signer: params.signer,
				signer_manager: params.signerManager,
				bond_index: params.bondIndex,
				reward_cycle: params.rewardCycle,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	private walkPox5Events(
		params: Pox5EventsWalkParams = {},
	): AsyncGenerator<IndexPox5Event> {
		return this.keysetWalk(
			params,
			(page) => this.listPox5Events({ ...params, ...page }),
			(e) => e.events,
		);
	}

	private async listPoxCycles(
		params: PoxCyclesListParams & RequestSignal = {},
	): Promise<PoxCyclesEnvelope> {
		return this.request<PoxCyclesEnvelope>(
			"GET",
			`/v1/index/pox/cycles${buildQuery({
				cursor: params.cursor,
				limit: params.limit,
			})}`,
			undefined,
			{ signal: params.signal },
		);
	}

	// PoX cycles page by a numeric `reward_cycle` cursor (descending) with no
	// from_cursor/from_height; the shared loop only ever sends `cursor`+`limit`.
	private walkPoxCycles(
		params: PoxCyclesWalkParams = {},
	): AsyncGenerator<IndexPoxCycle> {
		return this.keysetWalk<PoxCyclesEnvelope, IndexPoxCycle, number>(
			params,
			(page) =>
				this.listPoxCycles({
					cursor: page.cursor,
					limit: page.limit,
					signal: page.signal,
				}),
			(e) => e.cycles,
		);
	}

	private async getPoxCycle(
		rewardCycle: number,
	): Promise<PoxCycleEnvelope | null> {
		return this.requestOrNull<PoxCycleEnvelope>(
			"GET",
			`/v1/index/pox/cycles/${rewardCycle}`,
		);
	}
}
